import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Box,
} from '@mui/material';
import type { SerieListItem, SerieRequest } from '../types/serie';
import { initialSerieRequest, COMPROBANTES_VENTA } from '../types/serie';
import { useSerieStore } from '../store';

interface Props {
  open: boolean;
  editItem: SerieListItem | null;
  onClose: () => void;
}

export const SerieEditDialog: React.FC<Props> = ({ open, editItem, onClose }) => {
  const { saveSerie, loading } = useSerieStore();
  const [formData, setFormData] = useState<SerieRequest>(initialSerieRequest);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (editItem) {
      setFormData({
        idComprobante: editItem.idComprobante,
        serie: editItem.serie,
        proximoNumero: editItem.proximoNumero,
        esPrincipal: editItem.esPrincipal,
      });
    } else {
      setFormData(initialSerieRequest);
    }
    setError(null);
  }, [editItem, open]);

  const handleSubmit = async () => {
    if (formData.idComprobante === 0) {
      setError('Seleccione un tipo de comprobante');
      return;
    }
    if (!formData.serie.trim()) {
      setError('Ingrese la serie');
      return;
    }

    try {
      await saveSerie(formData, editItem?.idTalonario);
      onClose();
    } catch {
      // Error handled in store
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{editItem ? 'Editar Serie' : 'Nueva Serie'}</DialogTitle>
      <DialogContent>
        {error && (
          <Box sx={{ color: 'error.main', mb: 2, mt: 1 }}>{error}</Box>
        )}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <FormControl fullWidth size="small">
            <InputLabel>Tipo Comprobante</InputLabel>
            <Select
              value={formData.idComprobante}
              label="Tipo Comprobante"
              onChange={(e) => setFormData(prev => ({ ...prev, idComprobante: e.target.value as number }))}
            >
              <MenuItem value={0}>Seleccionar...</MenuItem>
              {COMPROBANTES_VENTA.map((c) => (
                <MenuItem key={c.idComprobante} value={c.idComprobante}>
                  {c.nombre}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="Serie"
            size="small"
            value={formData.serie}
            onChange={(e) => setFormData(prev => ({ ...prev, serie: e.target.value.toUpperCase() }))}
            placeholder="B001"
          />

          <TextField
            label="Proximo Numero"
            type="number"
            size="small"
            value={formData.proximoNumero}
            onChange={(e) => setFormData(prev => ({ ...prev, proximoNumero: Number(e.target.value) }))}
            slotProps={{ htmlInput: { min: 1 } }}
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={formData.esPrincipal}
                onChange={(e) => setFormData(prev => ({ ...prev, esPrincipal: e.target.checked }))}
              />
            }
            label="Es Principal"
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Cancelar</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={loading}>
          {loading ? 'Guardando...' : 'Guardar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
